import React, { useEffect, useState } from 'react'
import { useAppData } from '../../../../../../context/AppDataContext'
import VoucherOutItemForm from './VoucherOutItemForm';
import ConfirmDelete from '../../../../../../components/ConfirmDelete';
import { useAlert } from '../../../../../../context/AlertContext';
import { getGstBreakup } from '../gstHelper';
import voucherOutItemServices from '../voucherOutItemServices';
import VoucherTaxSummary from './VoucherTaxSummary';
import ledgerEntryServices from '../../ledger/ledgerEntryServices';
import VoucherOutItemSerialNumber from './VoucherOutItemSerialNumber';
import VoucherItemDescription from './VoucherItemDescription';

function VoucherOutItemList({ voucherDetails, productList, isPrinting, companyDetails }) {
    const { getMasterLabel } = useAppData();
    const { AppAlert } = useAlert();

    // Primary Products States
    const [primarySubTotal, setPrimarySubTotal] = useState(0);
    const [primaryGstTotal, setPrimaryGstTotal] = useState(0);

    // Secondary Charges / Services States
    const [secondarySubTotal, setSecondarySubTotal] = useState(0);
    const [secondaryGstTotal, setSecondaryGstTotal] = useState(0);

    // Consolidated Final Footer States
    const [roundOff, setRoundOff] = useState(0);
    const [finalGrandTotal, setFinalGrandTotal] = useState(0);

    const [itemList, setItemList] = useState([]);
    
    // State to toggle secondary items section if it's currently empty
    const [showSecondarySection, setShowSecondarySection] = useState(false);

    const getItemList = () => {
        voucherOutItemServices.getByVoucherId(voucherDetails.id).then(res => {
            setItemList(res.data);
        }).catch(error => {
            console.error("Error fetching items:", error);
        });
    }

    useEffect(() => {
        getItemList();
    }, [voucherDetails]);

    const calculateTotal = () => {
        if (itemList) {
            let _primarySub = 0;
            let _primaryGst = 0;
            let _secondarySub = 0;
            let _secondaryGst = 0;

            itemList.forEach(_item => {
                if (_item.isSecondary) {
                    _secondarySub += Number(_item.amount || 0);
                    _secondaryGst += Number(_item.gstAmount || 0);
                } else {
                    _primarySub += Number(_item.amount || 0);
                    _primaryGst += Number(_item.gstAmount || 0);
                }
            });

            setPrimarySubTotal(_primarySub.toFixed(2));
            setPrimaryGstTotal(_primaryGst.toFixed(2));

            setSecondarySubTotal(_secondarySub.toFixed(2));
            setSecondaryGstTotal(_secondaryGst.toFixed(2));

            const combinedRawTotal = _primarySub + _primaryGst + _secondarySub + _secondaryGst;
            const roundedTotal = Math.round(combinedRawTotal);
            const _roundOff = roundedTotal - combinedRawTotal;

            setRoundOff(_roundOff.toFixed(2));
            setFinalGrandTotal(roundedTotal);
        }
    }

    useEffect(() => {
        calculateTotal();
    }, [itemList]);

    const deleteItem = (id) => {
        voucherOutItemServices.remove(id).then(res => {
            AppAlert("Removed Successfully");
            getItemList();
        }).catch(error => {
            AppAlert(error.msg || "Error removing item", "error");
        });
    }

    const totalGstCombined = Number(primaryGstTotal) + Number(secondaryGstTotal);
    const taxRows = getGstBreakup(
        totalGstCombined,
        companyDetails?.state,
        //voucherDetails?.billingAddress?.supplyState
        voucherDetails.supplyState
    );

    const primaryItems = itemList.filter(item => !item.isSecondary);
    const secondaryItems = itemList.filter(item => item.isSecondary);

    // Condition flag to see if we even need to present a dual billing ecosystem layout
    const hasSecondaryItems = secondaryItems.length > 0;
    
    // Determine if the secondary section block itself should render
    const displaySecondaryBlock = hasSecondaryItems || (!isPrinting && voucherDetails.status === 1 && showSecondarySection);



    

    // 1. Maintain the base available limit fetched from the backend
    const [baseAvailableLimit, setBaseAvailableLimit] = useState(0);
    // 2. Maintain a separate state for the dynamic limit passed down to forms
    const [availableLimit, setAvailableLimit] = useState(0);

    const getAvailableLimit = () => {
        if (!voucherDetails?.ledgerId) return;
        
        ledgerEntryServices.getBalanceByLedgerId(voucherDetails.ledgerId)
            .then(res => {
                const limit = res.data.availableLimit || 0;
                setBaseAvailableLimit(limit);
                setAvailableLimit(limit - finalGrandTotal); // Set initial computation
            })
            .catch(error => {
                console.error("Error fetching ledger balance:", error);
            });
    };

    // Re-run API call only when the ledger/voucher changes
    useEffect(() => {
        getAvailableLimit();
    }, [voucherDetails?.ledgerId]);

    // Recalculate dynamic limit whenever the baseline or grand total changes
    useEffect(() => {
        setAvailableLimit(baseAvailableLimit - finalGrandTotal);
    }, [baseAvailableLimit, finalGrandTotal]);

    

    return (
        itemList && 
            <>
            <table className='table table-sm table-striped table-bordered mb-0' style={{ fontSize: 12 }}>
                <thead>
                    <tr>
                        <th>#</th>
                        <th>Description</th>
                        <th>HSN</th>
                        <th>Qty</th>
                        <th>UOM</th>
                        <th>Price</th>
                        <th>GST%</th>
                        <th>Amount</th>
                    </tr>
                </thead>
                
                {/* SECTION 1: PRIMARY PRODUCTS */}
                <tbody>
                    {/* Only show header if secondary items exist to make layout clean */}
                    {hasSecondaryItems && (
                        <tr className="table-light">
                            <td colSpan={8} className="fw-bold text-primary text-uppercase tracking-wide" style={{ letterSpacing: '0.5px' }}>
                                Primary Products / Core Items
                            </td>
                        </tr>
                    )}
                    {primaryItems.map((item, i) => (
                        <tr key={`primary-${i}`} className="hover-container">
                            <td>{i + 1}</td>
                            <td>
                                <div className="d-flex justify-content-between">
                                    
                                    <div>
                                        <div>
                                            <VoucherItemDescription voucherItem={item} isPrinting={isPrinting} getItemList={getItemList} />
                                        </div>
                                        <div>
                                            <VoucherOutItemSerialNumber voucherItem={item} isPrinting={isPrinting} />
                                        </div>
                                    </div>
                                    
                                    <div>
                                        {voucherDetails.status === 1 &&
                                            <ConfirmDelete id={item.id} confirm={deleteItem}>Are you sure you want to remove {item.title}</ConfirmDelete>
                                        }
                                    </div>
                                </div>
                            </td>
                            <td>
                                {item.description === null && item.hsn}
                                {item.description !== null && JSON.parse(item.description).hsn}
                            </td>
                            <td>{item.qty}</td>
                            <td>{getMasterLabel("product-uom", item.uom)}</td>
                            <td>{voucherDetails.voucherType === "Sale" ? item.price : ""}</td>
                            <td>{voucherDetails.voucherType === "Sale" ? item.gstRate : ""}</td>
                            <td>{voucherDetails.voucherType === "Sale" ? item.amount : ""}</td>
                        </tr>
                    ))}
                    {!isPrinting && voucherDetails.status === 1 && (
                        <tr>
                            <td></td>
                            <td colSpan={5}>
                                <VoucherOutItemForm 
                                    voucherDetails={voucherDetails} 
                                    callBack={getItemList} 
                                    productList={productList} 
                                    isSecondary={false}
                                    availableLimit={availableLimit}
                                />
                            </td>
                            <td></td>
                            <td></td>
                        </tr>
                    )}
                    {/* Intermediate Primary Product Total - Hidden if no secondary items present */}
                    {voucherDetails.voucherType === "Sale" && hasSecondaryItems && (
                        <tr className="table-warning-subtle text-dark">
                            <td colSpan={7} className="text-end fw-bold fst-italic">Total (Excl. Tax):</td>
                            <td className="fw-bold">{primarySubTotal}</td>
                        </tr>
                    )}
                </tbody>

                {/* OPTIONAL BUTTON: Appears only when empty and not editing/printing */}
                {!hasSecondaryItems && !showSecondarySection && !isPrinting && voucherDetails.status === 1 && (
                    <tbody>
                        <tr>
                            <td colSpan={8} className="text-center py-2 bg-light">
                                <button 
                                    type="button" 
                                    className="btn btn-sm btn-outline-success fw-bold"
                                    onClick={() => setShowSecondarySection(true)}
                                >
                                    + Add Extra Charges & Services (Logistics, Loading, etc.)
                                </button>
                            </td>
                        </tr>
                    </tbody>
                )}

                {/* SECTION 2: SECONDARY CHARGES & SERVICES */}
                {displaySecondaryBlock && (
                    <tbody>
                        <tr className="table-light">
                            <td colSpan={8} className="fw-bold text-success text-uppercase tracking-wide" style={{ letterSpacing: '0.5px' }}>
                                Extra Charges & Services (Logistics, Loading, Consumables)
                            </td>
                        </tr>
                        {secondaryItems.map((item, i) => (
                            <tr key={`secondary-${i}`} className="hover-container">
                                <td>{i + 1}</td>
                                <td>
                                    <div className="d-flex justify-content-between">
                                        <div>
                                            <div>
                                                <VoucherItemDescription voucherItem={item} isPrinting={isPrinting} getItemList={getItemList} />
                                            </div>
                                            <div>
                                                <VoucherOutItemSerialNumber voucherItem={item} isPrinting={isPrinting} />
                                            </div>
                                        </div>
                                        <div>
                                            {voucherDetails.status === 1 &&
                                                <ConfirmDelete id={item.id} confirm={deleteItem}>Are you sure you want to remove {item.title}</ConfirmDelete>
                                            }
                                        </div>
                                    </div>
                                </td>
                                <td>
                                    {item.description === null && item.hsn}
                                    {item.description !== null && JSON.parse(item.description).hsn}
                                </td>
                                <td>{item.qty}</td>
                                <td>{getMasterLabel("product-uom", item.uom)}</td>
                                <td>{voucherDetails.voucherType === "Sale" ? item.price : ""}</td>
                                <td>{voucherDetails.voucherType === "Sale" ? item.gstRate : ""}</td>
                                <td>{voucherDetails.voucherType === "Sale" ? item.amount : ""}</td>
                            </tr>
                        ))}
                        {!isPrinting && voucherDetails.status === 1 && (
                            <tr>
                                <td></td>
                                <td colSpan={5}>
                                    <VoucherOutItemForm 
                                        voucherDetails={voucherDetails} 
                                        callBack={getItemList} 
                                        productList={productList} 
                                        isSecondary={true}
                                        availableLimit={availableLimit}
                                    />
                                </td>
                                <td></td>
                                <td></td>
                            </tr>
                        )}
                        {/* Intermediate Secondary Charges Total - Only visible if there are elements */}
                        {voucherDetails.voucherType === "Sale" && hasSecondaryItems && (
                            <tr className="table-light">
                                <td colSpan={7} className="text-end fw-bold fst-italic">Extra Total (Excl. Tax):</td>
                                <td className="fw-bold">{secondarySubTotal}</td>
                            </tr>
                        )}
                    </tbody>
                )}

                {/* GLOBAL FINAL SUMMARY FOOTER */}
                {voucherDetails.voucherType === "Sale" && (
                    <tfoot style={{display: "table-row-group"}}>
                        <tr className="table-secondary">
                            <th colSpan={7}>
                                <div className='float-end fw-bold'>
                                    {hasSecondaryItems ? "Total Taxable Value (Combined):" : "Total Taxable Value:"}
                                </div>
                            </th>
                            <th className="fw-bold">{(Number(primarySubTotal) + Number(secondarySubTotal)).toFixed(2)}</th>
                        </tr>
                        
                        {/* DYNAMIC GST BREAKUP ROWS */}
                        {!voucherDetails.supplyState && (
                            <tr>
                                <td colSpan={8} className="text-center py-1">
                                    <span className="badge bg-warning text-dark">
                                        Please select a Billing Address to finalize GST calculation
                                    </span>
                                </td>
                            </tr>
                        )}
                        {voucherDetails.supplyState && taxRows.map((row, index) => (
                            <tr key={index} className={index % 2 === 0 ? 'table-light' : ''}>
                                <th colSpan={7}><div className='float-end fst-italic'>{row.label}</div></th>
                                <th>{row.amount}</th>
                            </tr>
                        ))}

                        {/* ROUND OFF ROW */}
                        {Number(roundOff) !== 0 && (
                            <tr>
                                <th colSpan={7}><div className='float-end fst-italic'>Round Off</div></th>
                                <th>{roundOff}</th>
                            </tr>
                        )}
                            
                        {/* COMBINED FINAL GRAND TOTAL */}
                        <tr className='table-dark'>
                             <th colSpan={7}>
                                <div className='float-end fs-6 text-uppercase fw-bold'>
                                    {hasSecondaryItems ? "Grand Total (Inc. All Items & Taxes)" : "Grand Total"}
                                </div>
                             </th>
                             <th className="fs-6 fw-bold">{finalGrandTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</th>
                        </tr>
                    </tfoot>
                )}
            </table>
            {voucherDetails.voucherType === "Sale" &&
                <VoucherTaxSummary itemList={itemList} />
            }
        </>
    );
}

export default VoucherOutItemList;